import { getData, postData } from "@/request/api";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface HeroData {
  image: string | null;
  /** Photos at the top of the home page (full URLs): 1 = static, 2+ = slider */
  slides?: string[];
  title: { ar: string; en: string };
  subtitle: { ar: string; en: string };
}

export interface CategoryData {
  id?: string;   // after transformDocs (_id → id)
  _id?: string;  // raw from DB (fallback)
  name: { ar: string; en: string };
  image: string | null;
}

export interface ExperienceData {
  _id: string;
  title: { ar: string; en: string };
  description: { ar: string; en: string };
  price: number;
  city?: string;
  dateFrom?: string;
  dateTo?: string;
  maxPeople?: number;
  categoryId: {
    _id: string;
    name: { ar: string; en: string };
    image: string | null;
  } | null;
  mainImage: string | null;
  gallery: string[];
  hostName: string;
  location: { lat: number; lng: number; address: string };
  schedules: {
    date: string;
    startTime: string;
    endTime: string;
    maxBookings: number;
  }[];
}

export interface SectionData {
  image: string | null;
  title: { ar: string; en: string };
  subtitle: { ar: string; en: string };
}

export interface ServiceData {
  id: string;
  slug?: string;
  title: { ar: string; en: string };
  provider: string;
  description: { ar: string; en: string };
  image: string | null;
  country: string;
  region: string;
  packages: {
    title: { ar: string; en: string };
    description: { ar: string; en: string };
    price: number;
    image: string | null;
  }[];
}

export interface PropertyFilterData {
  _id: string;
  name: { ar: string; en: string };
  icon: string;
}

export interface ServiceFilterData {
  _id: string;
  name: { ar: string; en: string };
  icon: string;
}

export interface PublicPropertyData {
  _id: string;
  slug: string;
  title: { ar: string; en: string };
  description: { ar: string; en: string };
  pricePerNight: number;
  guests: number;
  bedrooms: number;
  bathrooms: number;
  location: { lat: number; lng: number; address: string };
  region: { ar: string; en: string; code: string };
  images: string[];
  /** Photos grouped by room (Airbnb-style tour). */
  photoTour?: { title: { ar: string; en: string }; images: string[] }[];
  /** Internal unit code, e.g. JEZA8116-1-1 (shown under the name). */
  unitCode?: string;
  /** Unit page only: price of each of the next 365 nights. */
  nightlyPrices?: Record<string, number>;
  /** Same nights before the unit's discount (only when it has one). */
  nightlyBasePrices?: Record<string, number>;
  /** Public discount («خصم عام»); pricePerNight is already after it. */
  discount?: { percent: number; start?: string; end?: string; active?: boolean } | null;
  priceBeforeDiscount?: number;
  amenities?: { icon: string; name: { ar: string; en: string } }[];
  addOns?: { name: { ar: string; en: string }; price: number }[];
  ownerId: { username: string; email: string; phone: string } | null;
  filterId: {
    _id: string;
    name: { ar: string; en: string };
    icon: string;
  } | null;
  createdAt: string;
}

export interface PublicPropertiesResponse {
  data: PublicPropertyData[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

// ── Service ───────────────────────────────────────────────────────────────────

export interface TestimonialData {
  _id: string;
  name: { ar: string; en: string };
  subtitle: { ar: string; en: string };
  text: { ar: string; en: string };
  rating: number;
  avatar?: string;
}

export const publicService = {
  async getHero(): Promise<HeroData | null> {
    try {
      const res = await getData("public/hero");
      return res.data?.data ?? null;
    } catch {
      return null;
    }
  },

  async getCategories(): Promise<CategoryData[]> {
    try {
      const res = await getData("public/categories");
      return res.data?.data ?? [];
    } catch {
      return [];
    }
  },

  async getExperienceSection(): Promise<SectionData | null> {
    try {
      const res = await getData("public/experiences/section");
      return res.data?.data ?? null;
    } catch {
      return null;
    }
  },

  async getExperiences(
    params: { page?: number; limit?: number; categoryId?: string } = {},
  ): Promise<{ data: ExperienceData[]; pagination: any }> {
    try {
      const res = await getData("public/experiences", params);
      return res.data;
    } catch {
      return {
        data: [],
        pagination: { total: 0, page: 1, limit: 10, totalPages: 0 },
      };
    }
  },

  async getExperience(id: string): Promise<ExperienceData | null> {
    try {
      const res = await getData(`public/experiences/${id}`);
      return res.data?.data ?? null;
    } catch {
      return null;
    }
  },

  async getServiceSection(): Promise<SectionData | null> {
    try {
      const res = await getData("public/services/section");
      return res.data?.data ?? null;
    } catch {
      return null;
    }
  },

  async getServices(
    params: { page?: number; limit?: number; filterId?: string } = {},
  ): Promise<{ data: ServiceData[]; pagination: any }> {
    try {
      const res = await getData("public/services", params);
      return res.data;
    } catch {
      return {
        data: [],
        pagination: { total: 0, page: 1, limit: 10, totalPages: 0 },
      };
    }
  },

  async getService(id: string): Promise<ServiceData | null> {
    try {
      const res = await getData(`public/services/${id}`);
      return res.data?.data ?? null;
    } catch {
      return null;
    }
  },

  async getPropertyFilters(): Promise<PropertyFilterData[]> {
    try {
      const res = await getData("public/property-filters");
      return res.data?.data ?? [];
    } catch {
      return [];
    }
  },

  async getServiceFilters(): Promise<ServiceFilterData[]> {
    try {
      const res = await getData("public/service-filters");
      return res.data?.data ?? [];
    } catch {
      return [];
    }
  },

  async getProperties(
    params: {
      page?: number;
      limit?: number;
      search?: string;
      sort?: string;
      filterId?: string;
      minPrice?: number;
      maxPrice?: number;
      city?: string;
    } = {},
  ): Promise<PublicPropertiesResponse> {
    try {
      const res = await getData("public/properties", params);
      return res.data;
    } catch {
      return {
        data: [],
        pagination: {
          total: 0,
          page: 1,
          limit: 10,
          totalPages: 0,
          hasNextPage: false,
          hasPrevPage: false,
        },
      };
    }
  },

  // ── Testimonials (home "آراء العملاء") ─────────────────────────────────────
  async getPricingRates(): Promise<{ serviceFeePercent: number; vatPercent: number } | null> {
    try {
      const res = await getData("public/pricing");
      return res.data?.data ?? null;
    } catch {
      return null;
    }
  },

  async getTestimonials(): Promise<TestimonialData[]> {
    try {
      const res = await getData("public/testimonials");
      return res.data?.data ?? [];
    } catch {
      return [];
    }
  },

  async getRoomServices(): Promise<any[]> {
    try {
      const res = await getData("public/room-services");
      return res.data?.data ?? [];
    } catch {
      return [];
    }
  },

  async getPropertyCities(): Promise<{ ar: string; en: string }[]> {
    try {
      const res = await getData("public/property-cities");
      return res.data?.data ?? [];
    } catch {
      return [];
    }
  },

  async getProperty(id: string): Promise<PublicPropertyData | null> {
    try {
      const res = await getData(`public/properties/${id}`);
      return res.data?.data ?? null;
    } catch {
      return null;
    }
  },

  async getActiveDiscounts(): Promise<{
    _id: string;
    code: string;
    type: "percentage" | "fixed";
    value: number;
    description: string;
    endDate: string;
  }[]> {
    try {
      const res = await getData("public/discounts/active");
      return res.data?.data ?? [];
    } catch {
      return [];
    }
  },

  // ── About ─────────────────────────────────────────────────────────────────
  async getAbout(): Promise<AboutData | null> {
    try {
      const res = await getData("public/about");
      return res.data?.data ?? null;
    } catch {
      return null;
    }
  },

  // ── Terms ─────────────────────────────────────────────────────────────────
  async getTerms(): Promise<TermsData | null> {
    try {
      const res = await getData("public/terms");
      return res.data?.data ?? null;
    } catch {
      return null;
    }
  },

  // ── Blogs ─────────────────────────────────────────────────────────────────
  async getBlogs(
    params: { page?: number; limit?: number; search?: string } = {},
  ): Promise<{ data: BlogData[]; pagination: any }> {
    try {
      const res = await getData("public/blogs", params);
      return res.data;
    } catch {
      return { data: [], pagination: { total: 0, page: 1, limit: 12, totalPages: 0 } };
    }
  },

  async getBlog(idOrSlug: string): Promise<BlogData | null> {
    try {
      const res = await getData(`public/blogs/${idOrSlug}`);
      return res.data?.data ?? null;
    } catch {
      return null;
    }
  },

  // ── Contact ───────────────────────────────────────────────────────────────
  async getContact(): Promise<ContactData | null> {
    try {
      const res = await getData("public/contact");
      return res.data?.data ?? null;
    } catch {
      return null;
    }
  },

  async sendContactMessage(payload: {
    name: string;
    email?: string;
    phone?: string;
    subject?: string;
    message: string;
  }): Promise<{ success: boolean; message: string }> {
    const res = await postData("public/contact/messages", payload);
    return res.data;
  },

  /** «استكشف الوحدات» banner from the dashboard (null → the built-in look). */
  async getPropertiesBanner(): Promise<PropertiesBannerData | null> {
    try {
      const res = await getData("public/properties-banner");
      const d = res.data?.data;
      return d?.active ? d : null;
    } catch {
      return null;
    }
  },
};

export interface PropertiesBannerData {
  active: boolean;
  image: string | null;
  title: { ar: string; en: string };
  subtitle: { ar: string; en: string };
  showLogo: boolean;
}

// ── Content types ─────────────────────────────────────────────────────────────

export interface AboutData {
  heroImage: string | null;
  title: { ar: string; en: string };
  subtitle: { ar: string; en: string };
  content: { ar: string; en: string };
  vision?: { ar: string; en: string };
  mission?: { ar: string; en: string };
  storyImage: string | null;
  storyTitle: { ar: string; en: string };
  storyContent: { ar: string; en: string };
  stats: { value: string; label: { ar: string; en: string } }[];
  values: {
    icon: string;
    title: { ar: string; en: string };
    description: { ar: string; en: string };
  }[];
  gallery?: string[];
  team?: {
    photo: string | null;
    name: { ar: string; en: string };
    role: { ar: string; en: string };
  }[];
  cta?: {
    title: { ar: string; en: string };
    subtitle: { ar: string; en: string };
    buttonLabel: { ar: string; en: string };
    buttonLink: string;
  };
  sections?: Record<string, boolean>;
}

export interface TermsData {
  title: { ar: string; en: string };
  subtitle: { ar: string; en: string };
  categories: {
    title: { ar: string; en: string };
    content: { ar: string; en: string };
  }[];
}

export interface BlogData {
  _id: string;
  id?: string;
  title: { ar: string; en: string };
  slug: string;
  excerpt: { ar: string; en: string };
  content: { ar: string; en: string };
  coverImage: string | null;
  category: { ar: string; en: string };
  author: string;
  readMinutes: number;
  createdAt: string;
}

export interface ContactData {
  emails: string[];
  phones: { label: { ar: string; en: string } | string; number: string }[];
  socials: { platform: string; url: string }[];
  address: { ar: string; en: string };
  workingHours: { ar: string; en: string };
  location: { lat: number; lng: number; address: string };
}
